import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, Field } from "@/lib/assets";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const blank = { name: "", address: "", zip_code: "", city: "", province: "", notes: "" };

const SiteDialog = ({ open, onOpenChange, site, companyId, onDone }) => {
  const [f, setF] = useState(blank);
  useEffect(() => { if (open) setF(site ? Object.fromEntries(Object.keys(blank).map((k) => [k, site[k] || ""])) : blank); }, [open, site]);
  const s = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v || null]));
    try { if (site) await api.patch(`/crm/companies/${companyId}/sites/${site.id}`, body); else await api.post(`/crm/companies/${companyId}/sites`, body);
      toast.success("Sede salvata"); onOpenChange(false); onDone(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="sm:max-w-md" data-testid="site-dialog">
      <DialogHeader><DialogTitle>{site ? "Modifica sede" : "Nuova sede"}</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <Field l="Nome sede" className="col-span-2"><Input value={f.name} onChange={s("name")} placeholder="Es. Sede operativa Brescia" data-testid="site-name" /></Field>
        <Field l="Indirizzo" className="col-span-2"><Input value={f.address} onChange={s("address")} data-testid="site-address" /></Field>
        <Field l="CAP"><Input value={f.zip_code} onChange={s("zip_code")} maxLength={10} data-testid="site-zip" /></Field>
        <Field l="Città"><Input value={f.city} onChange={s("city")} data-testid="site-city" /></Field>
        <Field l="Provincia"><Input value={f.province} onChange={s("province")} maxLength={4} data-testid="site-province" /></Field>
        <Field l="Note" className="col-span-2"><Input value={f.notes} onChange={s("notes")} data-testid="site-notes" /></Field>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button><Button disabled={!f.name.trim()} onClick={save} data-testid="site-dialog-save">Salva</Button></DialogFooter>
    </DialogContent></Dialog>
  );
};

export const CompanySitesTab = ({ company }) => {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(undefined);
  const load = useCallback(() => api.get(`/crm/companies/${company.id}/sites`).then((r) => setItems(r.data.items)).catch(() => setItems([])), [company.id]);
  useEffect(() => { load(); }, [load]);
  const manage = can("companies:update") && !company.archived_at;
  const patch = async (site, body, msg) => { try { await api.patch(`/crm/companies/${company.id}/sites/${site.id}`, body); toast.success(msg); load(); } catch (e) { toast.error(errorMessage(e)); } };
  return (
    <Panel className="p-5" data-testid="company-sites-tab">
      <div className="mb-3 flex items-center justify-between"><SectionLabel>Sedi del cliente</SectionLabel>
        {manage && <Button size="sm" className="gap-1" onClick={() => setEdit(null)} data-testid="site-new"><Plus className="h-4 w-4" />Nuova sede</Button>}</div>
      {items && !items.length && <Empty testId="sites-empty">Nessuna sede registrata.</Empty>}
      <ul className="divide-y divide-line">{(items || []).map((s) => (
        <li key={s.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`site-row-${s.id}`}>
          <div className="min-w-0 flex-1"><p className={`text-sm font-semibold ${s.active === false ? "text-slate-400" : "text-ink"}`}>{s.name}</p>
            <p className="text-xs text-slate-500">{[s.address, s.zip_code, s.city, s.province].filter(Boolean).join(", ") || "Indirizzo non indicato"} · {s.usage.assets} asset · {s.usage.licenses} licenze</p></div>
          {s.is_primary && <StatusBadge tone="blue">Principale</StatusBadge>}
          {s.active === false && <StatusBadge tone="neutral">Disattiva</StatusBadge>}
          {manage && <>
            <Button size="sm" variant="outline" onClick={() => setEdit(s)} data-testid={`site-edit-${s.id}`}>Modifica</Button>
            {!s.is_primary && s.active !== false && <Button size="sm" variant="ghost" onClick={() => patch(s, { is_primary: true }, "Sede principale aggiornata")} data-testid={`site-primary-${s.id}`}>Rendi principale</Button>}
            <Button size="sm" variant="ghost" onClick={() => patch(s, { active: s.active === false }, s.active === false ? "Sede riattivata" : "Sede disattivata")} data-testid={`site-toggle-${s.id}`}>{s.active === false ? "Riattiva" : "Disattiva"}</Button>
          </>}
        </li>))}</ul>
      <SiteDialog open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)} site={edit} companyId={company.id} onDone={load} />
    </Panel>
  );
};
